import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  FormGroupComponent,
  MbsSize,
  ModalSettings,
  RadioComponent,
  RadioDescription
} from 'mbs-ui-kit';
import { ComputerModalsFacade } from 'mbs-ui-shared@facades/computer.modals.facade';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import Computer, { AgentType, OsType } from 'mbs-ui-shared@models/Computer';

import { FormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { AgentVersionForRestoreVerification } from 'mbs-ui-shared@models/backup/storages-type';
import { BetaFeatureBuildName } from 'mbs-ui-shared@services/beta-request.service';
import { FromToStepData, RestoreToVariants } from 'wizards/models/generic-restore/generic-restore-models';

const AgentVersionForUseToken = 875;
const AgentVersionForUseTokenString = '8.7.5';
const BetaFeatureBuildNameForRestoreToDifferentComputer = BetaFeatureBuildName.BackupBuilds;
const BetaFeatureNameForRestoreToDifferentComputer = 'restore-to-different-computer';

@Component({
  selector: 'mbs-generic-restore-from-to-step',
  templateUrl: './generic-restore-from-to-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CardComponent,
    CardGroupComponent,
    FormGroupComponent,
    FormsModule,
    NgbTooltipModule,
    RadioComponent,
    RadioDescription,
    I18NextPipe
  ]
})
export class GenericRestoreFromToStepComponent {
  private ability = inject(AbilityService);
  private i18Next = inject(I18NextPipe);
  private modalFacade = inject(ComputerModalsFacade);

  public readonly size = MbsSize;
  public readonly restoreToVariants = RestoreToVariants;
  private readonly originalComputerDefaultLabel = this.i18Next.transform('shared:generic.originalComputer');

  private computerValidator = (comp: Computer) =>
    comp?.online &&
    Computer.isAgentOnline(comp, AgentType.Backup) &&
    Computer.IsSupportedAgentVersion(comp, AgentType.Backup, AgentVersionForRestoreVerification);
  public computerValidatorError = this.i18Next.transform('shared:sidepanel-backup.backupStorages.browseStorage.computerIncorrect');
  public computersNotFound = this.i18Next.transform('shared:sidepanel-backup.backupStorages.browseStorage.computersNotFound');

  public canQuickRestore: WritableSignal<boolean> = signal(this.ability.can('read', PermissionsEnum.QuickRestore));

  public fromToStepData: OutputEmitterRef<FromToStepData> = output<FromToStepData>();
  public presetComputer = input<Computer>(null);
  private presetInitialized = false;

  public fromComputer: WritableSignal<Computer> = signal(null);
  public toComputer: WritableSignal<Computer> = signal(null);
  public canRestoreTo: WritableSignal<boolean> = signal(false);
  public restoreTo: WritableSignal<RestoreToVariants> = signal(RestoreToVariants.current);

  public isOnline: Signal<boolean> = computed(() => {
    const fromComputer = this.fromComputer();

    return fromComputer?.online && Computer.isAgentOnline(fromComputer, AgentType.Backup);
  });

  public isOnlineSecondComputer: Signal<boolean> = computed(() => {
    const toComputer = this.toComputer();

    return toComputer?.online && Computer.isAgentOnline(toComputer, AgentType.Backup);
  });

  public isSupportTokenSecondComputer: Signal<boolean> = computed(() => {
    const toComputer = this.toComputer();

    return toComputer && Computer.IsSupportedAgentVersion(toComputer, AgentType.Backup, AgentVersionForUseToken);
  });

  public fromComputerLabel: Signal<string> = computed(() => {
    const fromComputer = this.fromComputer();

    return fromComputer
      ? `${this.originalComputerDefaultLabel} (${fromComputer.name || fromComputer.computerName})`
      : this.originalComputerDefaultLabel;
  });

  public valid: Signal<boolean> = computed(() => {
    const fromComputer = this.fromComputer();
    const toComputer = this.toComputer();
    const restoreTo = this.restoreTo();
    const isOnline = this.isOnline();

    return !!(
      fromComputer &&
      ((restoreTo === RestoreToVariants.current && this.canQuickRestore()) ||
        (restoreTo === RestoreToVariants.original && isOnline) ||
        (restoreTo === RestoreToVariants.different && toComputer && this.isOnlineSecondComputer() && this.isSupportTokenSecondComputer()))
    );
  });

  constructor() {
    effect(() => {
      if (this.presetComputer() && !this.presetInitialized) {
        this.fromComputer.set(this.presetComputer());
        this.presetInitialized = true;
      }
    });

    effect(() => {
      const from = this.fromComputer();
      const to = this.toComputer();
      const restoreTo = this.restoreTo();

      if (!from || (restoreTo === RestoreToVariants.different && !to)) return;
      if (!this.canQuickRestore() && from.online && restoreTo === RestoreToVariants.current) {
        this.restoreTo.set(RestoreToVariants.original);
      }

      this.fromToStepData.emit({ restoreTo: this.restoreTo(), fromComputer: from, toComputer: to });
    });

    this.checkCanRestoreTo();
  }

  public differentSelectClickHandler(value: RestoreToVariants): void {
    this.restoreTo.set(value);
    if (value === RestoreToVariants.different) this.selectComputerClickHandler(false);
  }

  public selectComputerClickHandler(from = true): void {
    this.modalFacade
      .showSelectComputerModal(this.modalSettings(from))
      .subscribe((result) => result && this[from ? 'fromComputer' : 'toComputer'].set(result.items[0]));
  }

  private modalSettings(from: boolean): ModalSettings {
    const computer: Computer = this[from ? 'toComputer' : 'fromComputer']();

    return {
      size: MbsSize.md,
      data: {
        items: [],
        filter: from ? null : { os: [OsType.windows] },
        hiddenFilters: from ? null : { minAppVersion: AgentVersionForUseTokenString, appIds: [AgentType.Backup], online: true },
        disableOs: !from,
        saveText: this.i18Next.transform('shared:buttons.ok'),
        selectedAll: false,
        multipleSelect: false,
        showComputerDetails: true,
        notSetResult: true,
        ...(!from
          ? {
              betaFeatureBuildName: BetaFeatureBuildNameForRestoreToDifferentComputer,
              betaFeatureName: BetaFeatureNameForRestoreToDifferentComputer,
              betaFeatureVersion: AgentVersionForUseToken
            }
          : {}),
        computerValidator: !from ? this.computerValidator : null,
        computerValidatorError: !from ? this.computerValidatorError : null,
        computersNotFound: !from ? this.computersNotFound : null,
        excludeItem: computer ? { fieldName: 'hid', value: computer.hid } : null
      }
    };
  }

  private checkCanRestoreTo(): void {
    this.canRestoreTo.set(true);
  }
}
